import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Table from '../Components/Tables';
import Button from '../Components/Buttons';
import { MarkerApiProvider, useMarkerApi } from '../Services/MarkerApiProvider';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { useConfirmation } from '../Components/ConfirmationContext';
import { formatDateUTC } from '../utils/formatDateUTC';

export function MarkersContent({ canEdit }) {

    const { organizationId } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();
    const { getMarkers, deleteMarker } = useMarkerApi();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [alertShown, setAlertShown] = useState(false);
    const [items, setItems] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const columns = [ 
        { name: 'ID', selector: (row) => row.id, sortable: false },
        { name: 'Name', selector: (row) => row.name, sortable: false },
        { name: 'Status', selector: (row) => row.is_active === true ? "Active" : "InActive", sortable: false },
        { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
        { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
    ];

    const handleDelete = (row) => {
        showConfirmation(
            `Are you sure you want to delete the selected item?`,
            async () => {
                try {
                    setLoading(true);
                    await deleteMarker(row.id);
                    await fetchItems(0, itemsPerPage);
                    showAlert('success', 'Item was successfully deleted!');
                } catch (error) {
                    showAlert('fail', 'Failed to delete the item!');
                } finally {
                    setLoading(false);
                }
            }
        );
    }

    const handleRowClicked = (row) => {
        navigate(`/organizations/${organizationId}/markers/${row.id}`, { state: { organizationId } });
    }

    const fetchItems = async (skip, limit) => {
        if (limit !== itemsPerPage) {
            setItemsPerPage(limit);
        }

        setLoading(true);
        setError(null);
        try {
            const response = await getMarkers(skip, limit);
            setItems(response.items);
            setTotalItems(response.totalCount);
        } catch (error) {
            setError(error.message);
            showAlert('fail', "Failed to fetch markers");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchItems(0, itemsPerPage);
    }, []);

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-12">
                    {error ? (
                        <div>Error: {error}</div>
                    ) : (
                        <>
                            <Table
                                title="Markers"
                                columns={columns}
                                data={items}
                                selectableRows
                                selectableRowsHighlight
                                paginationPerPage={itemsPerPage}
                                progressPending={loading}
                                paginationTotalRows={totalItems}
                                onRowClicked={handleRowClicked}
                                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                                onDelete={handleDelete}
                                onChangePage={fetchItems}
                                getRowUrl={(row) => `/organizations/${organizationId}/markers/${row.id}`}
                                searchFields={['name']}
                                typeSearch = 'default'
                            />
                            {canEdit &&(
                                <Button
                                    background="color2"
                                    size="large"
                                    onClick={() => navigate(`/organizations/${organizationId}/markers/new-marker`)}
                                >
                                    Create New Marker
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export function Markers({ canEdit }) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <MarkerApiProvider>
                    <MarkersContent canEdit={canEdit} />
                </MarkerApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default Markers;
